import { useEffect, useState } from 'react';
import { RawGoal } from '@views';
import { EmptyFunction } from '@utils/common-types';
import { useCreateGoalAction } from '@actions/CreateGoalAction';
import { ActionResponse } from '@actions/ActionResponse';
import { DateTime } from 'luxon';
import { GroupDateMapper } from '@app/utils/GroupDateMapper';
import { useAllCohortUsersViewCreator } from '@app/view-creator/active-participants-view-creator';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { CohortTimelineIndexZero } from '@app/enums/CohortTimeline';
import { useActivityLookupViewCreator } from '@app/view-creator';

export const useUpdateGoalsDialogController = (groupId: number, refresh: EmptyFunction, groupStartDate: DateTime) => {
  const {
    data: activityLookupData,
    isLoading: isActivityLookupLoading,
    refetch: refetchActivityLookup,
  } = useActivityLookupViewCreator();
  const [showNewGoalDialog, setShowNewGoalDialog] = useState<boolean>(false);
  // True between a successful save and the user leaving the "Goal Set!" screen. Data isn't
  // refreshed until then, and the card keeps the dialog open even though goals now exist.
  const [isShowingSavedGoal, setIsShowingSavedGoal] = useState<boolean>(false);
  const { mutateAsync: createGoals } = useCreateGoalAction();
  const saveGoals = async (goals: RawGoal[]): Promise<ActionResponse> => {
    const response = await createGoals(goals);
    if (response.status === 'SUCCESS') {
      setIsShowingSavedGoal(true);
    } else {
      console.warn('Goal creation rejected by server:', response.statusMessage);
    }
    return response;
  };
  const closeDialog = () => {
    setShowNewGoalDialog(false);
    setIsShowingSavedGoal(false);
    refresh();
  };
  const { data: userData } = useAllCohortUsersViewCreator(groupId);
  const cookies = useAppCookies();
  const currentUserId = cookies.profile.id;
  const currentUser = userData?.allActiveCohortUsers?.find((p) => p.CohortUser.userId === currentUserId);
  const environment = import.meta.env.VITE_APP_ENV || 'development';

  useEffect(() => {
    // If the user is a coach, close the dialog (no goal setting)
    if (currentUser?.CohortUser.role === 'Coach') {
      setShowNewGoalDialog(false);
      return;
    }

    // If the user is a captain or admin, and we're *not* on a development environment, close the dialog (no goal setting)
    if (
      (currentUser?.CohortUser.role === 'Captain' || currentUser?.CohortUser.user.role === 'Admin') &&
      !['development', 'dev', 'test', 'local', 'staging'].includes(environment)
    ) {
      setShowNewGoalDialog(false);
      return;
    }

    // Get the current cohort day number
    const dateMapper = new GroupDateMapper(groupStartDate as DateTime<true>);
    const date = DateTime.local().toISODate();
    const currentCohortDayNumber = dateMapper.day(date);

    // Automatically show the goal dialog during the exercise period.
    // NOTE: The goal tracker card also confirms the user has no goals for the current period
    // and the survey drawer is not open, so this won't show on top of the goal survey drawer
    const showGoalDialog =
      currentCohortDayNumber > CohortTimelineIndexZero.Week0Day1 &&
      currentCohortDayNumber <= CohortTimelineIndexZero.LastExerciseDay;
    setShowNewGoalDialog(showGoalDialog);
  }, [userData]);

  // The dialog can't be shown correctly until both the activity lookup (options the dialog
  // renders) and the cohort users (the current user's role, which suppresses the dialog for
  // coaches/captains/admins) have resolved. Until then we're "not ready" and the dashboard
  // should keep its loader up rather than let the dialog flash open then closed.
  const isReady = !isActivityLookupLoading && !!activityLookupData && userData !== undefined;

  return {
    handleShowNewGoalDialog: () => setShowNewGoalDialog(true),
    showNewGoalDialog,
    isShowingSavedGoal,
    saveGoals,
    closeDialog,
    activityLookupData,
    isActivityLookupLoading,
    refetchActivityLookup,
    isReady,
  };
};
